import { MediaSrcFile, DeviationMediaItem } from './types';
import { PapiAttachmentFile, PapiDeviation } from '@wix/da-papi-types';
import { isValidFileSrc } from '../../DeviationViews/_helpers';

/**
 * Converts media file into src file ready for use
 *
 * "media": {
 *      "__spec" : "object, optional",
 *       "base_uri" : "string, optional",       - the url of original file without access tokens or resize params
 *       "pretty_name" : "string, optional",    - pretty name of the file, to be substituted into the command string
 *       "token" : [
 *           "__spec: array[scalar], optional"  - array of access tokens. there may be multiple tokens for various types based on access size restrictions and watermarking
 *       ],
 *       "types": [
 *           "__spec: array, optional",
 *           {
 *               "t"  : "string",              - type, one of
 *               "r"  : "integer",             - token array index. references array of tokens in media. special case values of -1 (not tokenized) and -2 (old school s3 url). to be appanded to base url
 *               "s"  : "string, optional",    - only applicable to old school s3 files or download original files on some deviation types such as pdf. contains entire url
 *               "c"  : "string, optional",    - command string, if exists. represents the resize or convert command. to be appended to the base uri.
 *               "h"  : "integer, optional",   - height
 *               "w"  : "integer, optional",   - width
 *               "o"  : "boolean, optional",   - transparency. only set if true. false is implied if if attribute is missing
 *               "f"  : "integer, optional",   - filesize. only set for 'preview' and 'fullview' types as it is meaningless for thumbs . (it will even be wrong for those if resized)
 *               "d"  : "integer, optional"    - duration. video only attribute
 *               "q"  : "string, optional"    - quality. video only attribute
 *               "b"  : "string, optional",    - only applicable to multi-file deviations such as gif. contains alternative base url. token index should still be uses
 *               "ss" : [                      - Set of images used for source set
 *                 "__spec: array, optional",
 *                 {
 *                    "x"  : "float", - density factor
 *                    "c"  : "string" - command string like above.
 *                 }
 *              ]
 *           }
 *       ]
 *   },
 *
 * @param deviation
 * @param file
 */
export function toMediaSrcFile(
  deviation: PapiDeviation | PapiAttachmentFile,
  file: DeviationMediaItem
): MediaSrcFile | undefined {
  if (!file) {
    return undefined;
  }

  const {
    t: type,
    r: token,
    s: oldSrc,
    c: command,
    h: height,
    w: width,
    o: transparency,
    q: quality,
    d: duration,
    f: filesize,
    b: multiFileSrc,
    ss: sourceset,
  } = file;

  const { baseUri, prettyName, token: tokens } = deviation.media!;

  const src = constructSrc({
    baseUri,
    multiFileSrc,
    prettyName,
    command,
    token,
    tokens,
    oldSrc,
  });

  let srcSet: string | undefined;

  if (sourceset && Array.isArray(sourceset) && sourceset.length > 0) {
    const srcItems: string[] = [];
    for (const srcItem of sourceset) {
      if (srcItem.w === width && srcItem.h === height) {
        continue; // never include srcset that is the same size as the parent
      }
      const srcAndDensity = constructSrc({
        baseUri,
        multiFileSrc,
        prettyName,
        command: srcItem.c,
        token,
        tokens,
        oldSrc,
      });
      if (srcAndDensity) {
        srcItems.push(`${srcAndDensity} ${srcItem.x}x`);
      }
    }
    if (srcItems.length > 0) {
      srcSet = srcItems.join(', ');
    }
  }

  return {
    src,
    type,
    width,
    height,
    transparency,
    quality,
    duration,
    filesize,
    isDefault: false,
    srcSet,
  };
}

function constructSrc({
  baseUri,
  multiFileSrc,
  prettyName,
  command,
  token,
  tokens,
  oldSrc,
}: any) {
  let src: string = baseUri!;

  if (multiFileSrc) {
    src = multiFileSrc;
  }

  // Append the command
  if (command) {
    // Command will include the separator in the future
    const separator = ['/', ':'].includes(command[0]) ? '' : '/';
    src = `${src}${separator}${command.replace(/<prettyName>/g, prettyName!)}`;
  }

  // Append the token
  if (token >= 0 && tokens) {
    src = `${src}?token=${tokens[token]}`;
  }

  // Handle old things
  if (token === -2 && oldSrc) {
    src = oldSrc!;
  }

  if (!isValidFileSrc(src)) {
    src = '';
  }

  return src;
}

export const fallbackToLargestFile = (t: MediaSrcFile[]) => t[t.length - 1]; // Take largest if nothing covers
